import { FaHandPaper } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { socketMessageType } from "types/Constants";
import { validateAndCreateSocketIfExist } from "utils/SocketValidator";
import { socketContextType } from "index";
import { useContext, useEffect, useRef } from "react";
import SocketContext from "socket-context";
import { UserInfoMapperSocketHandler } from "utils/UserInfoMapper";
import axiosInstance from "config/axios";
import { RootState } from "store";
import { hideQueryPopup, raiseQuery, showQueryPopup, unraiseQuery, updateLiveStreamSettings } from "store/reducers/livestreamSettings";
const Query = ({
  title,
  onClick,
  isActive = false,
}: {
  title: string;
  onClick: (query: string) => void;
  isActive?: boolean;
}) => {
  return (
    <div
      onClick={() => onClick(title)} style={{minWidth: "100%"}}
      className={`
        flex items-center justify-center gap-2 
        px-4 py-3 
        text-white text-sm font-medium 
        bg-[#24272C]  
        border border-white/10 
        rounded-full cursor-pointer 
        w-full
        max-w-xs
        hover:scale-105 transition-all duration-300 ease-out 
        
        /* Mobile first approach */
        mobile:text-sm mobile:px-3 mobile:py-2 mobile:min-h-[44px]  mobile:font-medium 
        
        /* Tablet and above */
        tablet:text-sm tablet:px-6 tablet:py-3
        tablet:shadow-[0_4px_12px_rgba(0,0,0,0.4)]
        tablet:hover:shadow-[0_6px_16px_rgba(0,0,0,0.5)]
        
        /* Larger screens */
        laptop:text-sm laptop:px-6 laptop:py-3
        laptop:shadow-[0_4px_12px_rgba(0,0,0,0.4)]
        laptop:hover:shadow-[0_6px_16px_rgba(0,0,0,0.5)]
        
        desktop:text-sm desktop:px-6 desktop:py-3
        desktop:shadow-[0_4px_12px_rgba(0,0,0,0.4)]
        desktop:hover:shadow-[0_6px_16px_rgba(0,0,0,0.5)]
        
        largescreen:text-sm largescreen:px-6 largescreen:py-3
        largescreen:shadow-[0_4px_12px_rgba(0,0,0,0.4)]
        largescreen:hover:shadow-[0_6px_16px_rgba(0,0,0,0.5)]

        ${isActive ? "bg-[#e8bd46] text-gray-900" : ""}
      `}
    >
      <FaHandPaper className="mobile:text-sm tablet:text-base laptop:text-base desktop:text-base largescreen:text-base" />
      <span className="text-center mobile:text-xs tablet:text-sm">{title}</span>
    </div>
  );
};


const StandardQueriesPanel = ({
  onSelect,
}: {
  onSelect?: (query: string) => void;
}) => {
  const queries = [
    "Raise a Hand",
    "Please repeat?",
    "Give us an example?",
    "Can I share one?",
    "Explain a bit more?",
    "Different perspective?",
    "Please go slower?",
  ];

  const userData = useSelector((state: any) => state.auth.user);
  const settings: any = useSelector((state: RootState) => state.live.settings);
  let userInfoHandler = new UserInfoMapperSocketHandler();
  const socketList: socketContextType[] = useContext(SocketContext);
  const { activeQueries } = useSelector((state: RootState) => state.live.settings);
  const dispatch = useDispatch();

  const timeoutRefs = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const saveActions = async (subType: string) => {
    const session = await axiosInstance.post(
      "/trainee/live_session_plan_preview_trainee",
      { session_map_id: settings.roomId }
    );

    const responseData = session?.data?.responseData || {};
    const allActivities = [
      ...(responseData.prePlannedActivities || []),
      ...(responseData.quickSetupActivities || []),
    ];

    const launchData = allActivities.find((el: any) => el.status === "LAUNCH");
    if (!launchData?.id) {
      return;
    }

    const engagementType =
      subType === "Raise a Hand" ? "L_RAISE_HAND" : "L_STANDARD_QUERIES";

    const obj = {
      engagementType,
      userId: userData?.id,
      sessionActivityExecutionLogId: launchData?.id,
      isRead: "NO",
      participantId: userData?.id,
      subType: subType === "Raise a Hand" ? "RAISE_HAND" : null,
    };

    await axiosInstance.post("/session/save_session_actions_log", obj);
  };

  const sendRaiseHand = async (query: string) => {
    const isRaiseHand = query === "Raise a Hand";

    const formattedTime = new Date().toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    let reactionType: string;
    let socketType: string;

    if (isRaiseHand) {
      reactionType = "raise_hand";
      socketType = socketMessageType?.RAISE_HAND;
    } else {
      reactionType = "std_query";
      socketType = socketMessageType?.RAISE_HAND;
    }

    userInfoHandler = validateAndCreateSocketIfExist(settings.roomId, () => { }, socketList);

    userInfoHandler.sendMessage(
      JSON.stringify({
        type: socketType,
        user_reaction_data: {
          reaction_type: reactionType,
          reacted_by_email: userData?.email,
          reacted_by_name: userData?.name,
          reacted_by_type: "trainee",
          query_message: query,
          time: formattedTime,
        },
        room_id: settings.roomId,
      })
    );

    await axiosInstance.post("/session/create_session_raised_hand", {
      session_map_id: Number(settings.roomId),
      content: JSON.stringify({
        reaction_type: reactionType,
        reacted_by_email: userData?.email,
        reacted_by_name: userData?.name,
        reacted_by_type: "trainee",
        query_message: reactionType === "std_query" ? query : null,
        time: formattedTime,
      }),
      trainee_id: userData?.id,
      type: reactionType,
    });
  };

  const sendLowerHand = async (queryType: string, originalQuery: string) => {
    const formattedTime = new Date().toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    userInfoHandler = validateAndCreateSocketIfExist(settings.roomId, () => { }, socketList);

    userInfoHandler.sendMessage(
      JSON.stringify({
        type: socketMessageType?.RAISE_HAND,
        user_reaction_data: {
          reaction_type: "lower_hand",
          reacted_by_email: userData?.email,
          reacted_by_name: userData?.name,
          reacted_by_type: "trainee",
          query_message: queryType === "raise_hand" ? "Raise a Hand" : originalQuery,
          time: formattedTime,
        },
        room_id: settings.roomId,
      })
    );

    const session = await axiosInstance.post(
      "/trainee/live_session_plan_preview_trainee",
      { session_map_id: settings.roomId }
    );

    const responseData = session?.data?.responseData || {};
    const allActivities = [
      ...(responseData.prePlannedActivities || []),
      ...(responseData.quickSetupActivities || []),
    ];

    const launchData = allActivities.find((el: any) => el.status === "LAUNCH");
    if (launchData?.id) {
      const engagementType = originalQuery === "Raise a Hand" ? "L_RAISE_HAND" : "L_STANDARD_QUERIES";

      const obj = {
        engagementType,
        userId: userData?.id,
        sessionActivityExecutionLogId: launchData?.id,
        isRead: "NO",
        participantId: userData?.id,
        subType: originalQuery === "Raise a Hand" ? "LOWER_HAND" : null,
      };

      await axiosInstance.post("/session/save_session_actions_log", obj);
    }

    await axiosInstance.post("/session/create_session_raised_hand", {
      session_map_id: Number(settings.roomId),
      content: JSON.stringify({
        reaction_type: "lower_hand",
        reacted_by_email: userData?.email,
        reacted_by_name: userData?.name,
        reacted_by_type: "trainee",
        query_message: queryType === "raise_hand" ? null : originalQuery,
        time: formattedTime,
      }),
      trainee_id: userData?.id,
      type: "lower_hand",
    });
  };

  const handleQueryToggle = async (query: string) => {
    const queryType = query === "Raise a Hand" ? "raise_hand" : query;
    onSelect?.(query);
    if (activeQueries[queryType]) {
      await handleUnraiseQuery(queryType, query);
    } else {
      dispatch(updateLiveStreamSettings({ raiseHand: false }))
      await handleRaiseQuery(queryType, query);
    }
  };

  const handleRaiseQuery = async (queryType: string, originalQuery: string) => {
    if (timeoutRefs.current[queryType]) {
      clearTimeout(timeoutRefs.current[queryType]);
      delete timeoutRefs.current[queryType];
    }

    const timeoutId = setTimeout(() => {
      console.log(`Auto-unraising query: ${queryType}`);
      handleUnraiseQuery(queryType, originalQuery, true);
    }, 20000);

    timeoutRefs.current[queryType] = timeoutId;

    dispatch(raiseQuery({ queryType, timeoutId }));

    dispatch(showQueryPopup({ queryType, originalQuery }));

    await sendRaiseHand(originalQuery);

    if (originalQuery !== "Raise a Hand") {
      await saveActions(originalQuery);
    }
  };

  const handleUnraiseQuery = async (queryType: string, originalQuery: string, isAutoUnraise = false) => {
    console.log(`Unraising query: ${queryType}, auto: ${isAutoUnraise}`);

    if (timeoutRefs.current[queryType]) {
      clearTimeout(timeoutRefs.current[queryType]);
      delete timeoutRefs.current[queryType];
    }

    await sendLowerHand(queryType, originalQuery);

    dispatch(unraiseQuery(queryType));

    dispatch(hideQueryPopup(queryType));
  };

  const isQueryActive = (query: string) => {
    const queryType = query === "Raise a Hand" ? "raise_hand" : query;
    return !!activeQueries[queryType];
  };

  const getQueryTitle = (query: string) => {
    const isActive = isQueryActive(query);
    if (query === "Raise a Hand") {
      return isActive ? "Lower Hand" : "Raise a Hand";
    }
    return isActive ? `Unraise: ${query}` : query;
  };

  useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      timeoutRefs.current = {};
    };
  }, []);

  return (
    <div
      className="
        flex flex-col items-center gap-4
        w-full
        
        /* Mobile layout */
        mobile:bg-[#24272C] mobile:p-3 mobile:min-h-screen
        
        /* Tablet and above */
        tablet:bg-[#24272C] tablet:p-6 tablet:rounded-lg tablet:shadow-lg
        tablet:min-h-0 tablet:gap-6
        
        /* Larger screens */
        laptop:bg-[#24272C] laptop:p-6
        desktop:bg-[#24272C] desktop:p-6
        largescreen:bg-[#24272C] largescreen:p-6
      "
    >
      {/* Main Raise Hand Button */}
      <div className="w-full flex justify-center mobile:mb-2 tablet:mb-0" style={{minWidth: "100%"}}>
        <Query
          onClick={() => handleQueryToggle("Raise a Hand")}
          title={getQueryTitle("Raise a Hand")}
          isActive={isQueryActive("Raise a Hand")}
        />
      </div>

      {/* Standard Queries Grid */}
      <div className="
        grid 
        mobile:grid-cols-1 mobile:gap-3 mobile:w-full mobile:px-2
        tablet:grid-cols-1 tablet:gap-4 tablet:w-full tablet:max-w-4xl tablet:px-0
        laptop:grid-cols-1 laptop:gap-4
        desktop:grid-cols-1 desktop:gap-4
        largescreen:grid-cols-1 largescreen:gap-4
        w-full max-w-4xl
      " style={{minWidth: "100%"}}>
        {queries.slice(1).map((query) => (
          <Query
            onClick={() => handleQueryToggle(query)}
            key={query}
            title={getQueryTitle(query)}
            isActive={isQueryActive(query)}
          />
        ))}
      </div>
    </div>
  );
};

export default StandardQueriesPanel;
